import fetchWithAuth from '../components/fetchWithAuth';
import config from '../config';
import { cachedRequest } from '../utils/requestCache';

const API_BASE = `${config.API_BASE_URL}/api/services`;
const CATEGORIES_API = `${config.API_BASE_URL}/api/services/categories`;

// Obtener todos los servicios (opcionalmente filtrados por sucursal o categoría)
// Comparte la petición en vuelo y cachea solo respuestas exitosas (TASK-122).
// signal cancela solo la espera de este consumidor.
export const getServices = (branchId = null, categoryId = null, signal) => {
    const params = new URLSearchParams();
    if (branchId) params.append('branchId', branchId);
    if (categoryId) params.append('categoryId', categoryId);
    const queryString = params.toString();
    const url = queryString ? `${API_BASE}?${queryString}` : API_BASE;

    return cachedRequest(url, async () => {
        const response = await fetchWithAuth(url);
        if (!response.ok) {
            if (response.status === 429) {
                console.warn('Rate limit 429 en getServices.');
            }
            // Lanzar para que el fallo no quede cacheado; abajo se traduce a []
            const httpError = new Error(`Error ${response.status}`);
            httpError.isHttpError = true;
            throw httpError;
        }
        const data = await response.json().catch(() => ([]));
        return Array.isArray(data) ? data : (data?.services || []);
    }, { signal }).catch((error) => {
        // Contrato previo: una respuesta HTTP no-ok devuelve lista vacía
        if (error.isHttpError) return [];
        throw error;
    });
};

// Obtener servicios públicos de una empresa (para la página de reservas)
// TASK-162: deduplica peticiones en vuelo y cachea temporalmente
export const getPublicServices = async (companyId, branchId = null, categoryId = null) => {
    const params = new URLSearchParams();
    if (branchId) params.append('branchId', branchId);
    if (categoryId) params.append('categoryId', categoryId);
    const queryString = params.toString();
    const url = queryString ? `${API_BASE}/public/${companyId}?${queryString}` : `${API_BASE}/public/${companyId}`;
    
    return cachedRequest(url, async () => {
        const response = await fetchWithAuth(url, { skipAuthRedirect: true });
        if (!response.ok) return [];
        return response.json();
    }, { ttl: 20000 });
};

// Crear un nuevo servicio
export const createService = async (serviceData) => {
    const response = await fetchWithAuth(API_BASE, {
        method: 'POST',
        body: JSON.stringify(serviceData)
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Error ${response.status} al crear servicio`);
    }
    return response.json();
};

// Actualizar un servicio existente
export const updateService = async (serviceId, serviceData) => {
    const response = await fetchWithAuth(`${API_BASE}/${serviceId}`, {
        method: 'PUT',
        body: JSON.stringify(serviceData)
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Error ${response.status} al actualizar servicio`);
    }
    return response.json();
};

// Eliminar (desactivar) un servicio
export const deleteService = async (serviceId) => {
    const response = await fetchWithAuth(`${API_BASE}/${serviceId}`, {
        method: 'DELETE'
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Error ${response.status} al eliminar servicio`);
    }
    return response.json();
};

// ==================== CATEGORÍAS Y ESPECIALIDADES ====================

// Obtener todas las categorías de la empresa
export const getCategories = async (includeInactive = false) => {
    const url = includeInactive ? `${CATEGORIES_API}?includeInactive=true` : CATEGORIES_API;
    const response = await fetchWithAuth(url);
    if (!response.ok) return [];
    const data = await response.json().catch(() => ({ categories: [] }));
    return data.categories || [];
};

// Obtener categorías públicas de una empresa (para la página de reservas)
// TASK-162: deduplica peticiones en vuelo y cachea temporalmente
export const getPublicCategories = async (companyId) => {
    const url = `${CATEGORIES_API}/public/${companyId}`;
    return cachedRequest(url, async () => {
        const response = await fetchWithAuth(url, { skipAuthRedirect: true });
        if (!response.ok) return [];
        const data = await response.json().catch(() => ({ categories: [] }));
        return data.categories || [];
    }, { ttl: 30000 });
};

// Crear categoría
export const createCategory = async (categoryData) => {
    const response = await fetchWithAuth(CATEGORIES_API, {
        method: 'POST',
        body: JSON.stringify(categoryData)
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Error ${response.status} al crear categoría`);
    }
    return response.json();
};

// Actualizar categoría
export const updateCategory = async (categoryId, categoryData) => {
    const response = await fetchWithAuth(`${CATEGORIES_API}/${categoryId}`, {
        method: 'PUT',
        body: JSON.stringify(categoryData)
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Error ${response.status} al actualizar categoría`);
    }
    return response.json();
};

// Eliminar categoría
export const deleteCategory = async (categoryId) => {
    const response = await fetchWithAuth(`${CATEGORIES_API}/${categoryId}`, {
        method: 'DELETE'
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Error ${response.status} al eliminar categoría`);
    }
    return response.json();
};

// ==================== ASIGNACIONES DE EMPLEADOS ====================

// Obtener empleados asignados a un servicio
export const getServiceEmployees = async (serviceId) => {
    const response = await fetchWithAuth(`${API_BASE}/${serviceId}/employees`);
    if (!response.ok) return [];
    const data = await response.json().catch(() => ({ employees: [] }));
    return data.employees || [];
};

// Obtener servicios asignados a un profesional
export const getEmployeeServices = async (employeeId) => {
    const response = await fetchWithAuth(`${API_BASE}/employee/${employeeId}`);
    if (!response.ok) return [];
    const data = await response.json().catch(() => ({ services: [] }));
    return data.services || [];
};
